import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../../lib/supabase'

export interface RewardItemBalance {
  rewardItemId: string
  name: string
  currentCount: number
}

interface RawRewardItem {
  id: string
  name: unknown
}

async function fetchMemberRewardData(memberId: string): Promise<{
  items: RawRewardItem[]
  countByItemId: Map<string, number>
}> {
  const [itemsResult, rewardsResult] = await Promise.all([
    supabase
      .from('reward_items')
      .select('id, name')
      .eq('active', true)
      .eq('programme', 'loyalty'),
    supabase
      .from('rewards')
      .select('reward_item_id')
      .eq('member_id', memberId)
      .eq('status', 'active'),
  ])

  if (itemsResult.error) throw itemsResult.error
  if (rewardsResult.error) throw rewardsResult.error

  const countByItemId = new Map<string, number>()
  for (const row of rewardsResult.data ?? []) {
    if (row.reward_item_id) {
      countByItemId.set(row.reward_item_id, (countByItemId.get(row.reward_item_id) ?? 0) + 1)
    }
  }

  return { items: (itemsResult.data ?? []) as RawRewardItem[], countByItemId }
}

export function useMemberRewardData(memberId: string | undefined) {
  const { i18n } = useTranslation()
  const lang = i18n.language === 'sv' ? 'sv' : 'en'

  return useQuery({
    queryKey: ['member-reward-data', memberId],
    queryFn: () => fetchMemberRewardData(memberId!),
    enabled: !!memberId,
    staleTime: 30 * 1000,
    select: (data): RewardItemBalance[] =>
      data.items.map((item) => {
        const nameObj = item.name as Record<string, string>
        return {
          rewardItemId: item.id,
          name: nameObj[lang] || nameObj.en || item.id,
          currentCount: data.countByItemId.get(item.id) ?? 0,
        }
      }),
  })
}